import { toJS } from "mobx";
import { useCallback, useEffect, useMemo, useState } from "react";
import { PWSSessionBackend, PwsMediaEpisode, getOsTypeFromAgent } from "@inferno/renderer-shared-core";
import { ILog } from "@ihr-radioedit/inferno-core";
import { Container } from "../../../../core/ui";
import "./CoastMedia.style.scss";
import { CoastMediaEpisodeNode, episodesToLinkedList } from "./CoastMedia.util";
import { CoastMediaEpisode } from "./CoastMediaEpisode.component";
import { CoastMediaPlayerLoader } from "./CoastMediaPlayerLoader.component";
import { CoastMediaPlayerSkeleton } from "./CoastMediaPlayerSkeleton.component";
import { EpisodesSkeletons } from "./CoastMediaTabsSkeleton.component";

const log = ILog.logger("CoastMediaPlaylist.component");

interface CoastMediaPlaylistProps {
  episodesInput: PwsMediaEpisode[];
  session: PWSSessionBackend;
  playerApiKey: string;
  zypeSlug?: string;
}

export const CoastMediaPlaylist = ({ episodesInput, playerApiKey, session, zypeSlug }: CoastMediaPlaylistProps) => {
  const episodes = useMemo(() => episodesToLinkedList(episodesInput), [episodesInput]);
  const episodesForTab = useMemo(() => episodes.getEpisodesForPage(1), [episodes]);
  const [currentEpisode, setCurrentEpisode] = useState<CoastMediaEpisodeNode | null>(null);
  const osType = useMemo(() => getOsTypeFromAgent(), []);

  const updateCurrentEpisode = useCallback(
    (episode: CoastMediaEpisodeNode, _?: string) => {
      if (episode.data.subscriptionRequired && !session.state.hasActiveSubscription) {
        session.executeOnValidSubscription(() => setCurrentEpisode(episode));
      } else {
        setCurrentEpisode(episode);
      }
    },
    [session],
  );

  const playNextEpisode = useCallback(async () => {
    log.debug("Play next episode", toJS(currentEpisode));

    if (currentEpisode?.next) {
      updateCurrentEpisode(currentEpisode?.next);
    }
  }, [currentEpisode, updateCurrentEpisode]);

  useEffect(() => {
    const episodeOverride = episodesForTab.find(episode => episode.data.slug === zypeSlug);
    if (episodeOverride) {
      updateCurrentEpisode(episodeOverride);
    } else if (episodes.head) {
      updateCurrentEpisode(episodes.head);
    }
  }, [episodes, updateCurrentEpisode, episodesForTab, zypeSlug]);

  return (
    <Container className="component-coast-media-playlist">
      {currentEpisode ? (
        <CoastMediaPlayerLoader
          playNextEpisode={playNextEpisode}
          playerApiKey={playerApiKey}
          episode={currentEpisode.data}
          session={session}
          osType={osType}
        />
      ) : (
        <CoastMediaPlayerSkeleton />
      )}

      <div className="episodes-container">
        {episodesForTab.length ? (
          episodesForTab.map(episode => (
            <CoastMediaEpisode
              setCurrentEpisode={updateCurrentEpisode}
              groupSlug={"insider-audio"}
              key={episode.data.slug}
              episode={episode}
            />
          ))
        ) : (
          <EpisodesSkeletons count={4} />
        )}
      </div>
    </Container>
  );
};
